import React, { useMemo } from 'react';
import { FaChartBar, FaChartPie, FaExclamationTriangle } from 'react-icons/fa';
import { VoIPDevice, StationStats } from '../utils/voipTypes';

interface VoIPChartsProps {
  stationStats: StationStats[];
  devices: VoIPDevice[];
}

const VoIPCharts: React.FC<VoIPChartsProps> = ({ stationStats, devices }) => {
  // Calculate status distribution (Reachable/Unreachable)
  const statusDistribution = useMemo(() => ({
    reachable: devices.filter(d => d.status === 'online').length,
    unreachable: devices.filter(d => d.status === 'offline').length,
  }), [devices]);

  // Get unreachable devices grouped by station
  const unreachableByStation = useMemo(() => {
    const stationMap = new Map<string, { station: string; count: number; devices: string[] }>();

    devices.filter(d => d.status === 'offline').forEach(device => {
      const station = device.station || 'Unknown';
      const existing = stationMap.get(station);
      if (existing) {
        existing.count++;
        existing.devices.push(device.name);
      } else {
        stationMap.set(station, { station, count: 1, devices: [device.name] });
      }
    });

    return Array.from(stationMap.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Top 10 stations with unreachable devices
  }, [devices]);

  // Get devices with high latency (> 100ms)
  const highLatencyDevices = useMemo(() => {
    return devices
      .filter(d => d.status === 'online' && d.latency > 100)
      .sort((a, b) => b.latency - a.latency)
      .slice(0, 10);
  }, [devices]);

  const maxUnreachable = Math.max(...unreachableByStation.map(s => s.count), 1);
  const maxLatency = Math.max(...highLatencyDevices.map(d => d.latency), 200);

  return (
    <div className="voip-charts-row">
      {/* Reachability Status Chart */}
      <div className="chart-card">
        <div className="chart-title">
          <h3><FaChartPie /> Device Reachability</h3>
        </div>
        <div className="donut-chart-container">
          <div className="donut-chart">
            <svg viewBox="0 0 100 100">
              {devices.length > 0 ? (
                <>
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#28a745"
                    strokeWidth="15"
                    strokeDasharray={`${(statusDistribution.reachable / devices.length) * 251.2} 251.2`}
                    transform="rotate(-90 50 50)"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#dc3545"
                    strokeWidth="15"
                    strokeDasharray={`${(statusDistribution.unreachable / devices.length) * 251.2} 251.2`}
                    strokeDashoffset={`${-(statusDistribution.reachable / devices.length) * 251.2}`}
                    transform="rotate(-90 50 50)"
                  />
                </>
              ) : (
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#e0e0e0"
                  strokeWidth="15"
                />
              )}
            </svg>
            <div className="donut-center">
              <span className="donut-value">
                {devices.length > 0 ? Math.round((statusDistribution.reachable / devices.length) * 100) : 0}%
              </span>
              <span className="donut-label">Reachable</span>
            </div>
          </div>
          <div className="status-legend">
            <div className="legend-row">
              <span className="dot online" />
              <span className="label">Reachable</span>
              <span className="value">{statusDistribution.reachable}</span>
            </div>
            <div className="legend-row">
              <span className="dot offline" />
              <span className="label">Unreachable</span>
              <span className="value">{statusDistribution.unreachable}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Unreachable Devices by Station */}
      <div className="chart-card">
        <div className="chart-title">
          <h3><FaExclamationTriangle /> Unreachable by Station</h3>
        </div>
        <div className="station-chart">
          {unreachableByStation.length === 0 ? (
            <div className="no-chart-data">All devices are reachable</div>
          ) : (
            unreachableByStation.map((station) => (
              <div key={station.station} className="station-bar-row">
                <div className="station-label" title={station.devices.join(', ')}>
                  {station.station}
                </div>
                <div className="station-bar-container">
                  <div
                    className="station-bar offline"
                    style={{ width: `${(station.count / maxUnreachable) * 100}%` }}
                  />
                </div>
                <div className="station-count">
                  <span className="offline">{station.count}</span>
                </div>
              </div>
            ))
          )}
        </div>
        {unreachableByStation.length > 0 && (
          <div className="chart-footer">
            <span className="footer-text">
              Total: {statusDistribution.unreachable} unreachable devices
            </span>
          </div>
        )}
      </div>

      {/* High Latency Devices */}
      <div className="chart-card">
        <div className="chart-title">
          <h3><FaChartBar /> High Latency Devices (&gt;100ms)</h3>
        </div>
        <div className="latency-chart">
          {highLatencyDevices.length === 0 ? (
            <div className="no-chart-data">No high latency devices</div>
          ) : (
            <div className="latency-bars">
              {highLatencyDevices.map((device) => {
                const latencyPercent = (device.latency / maxLatency) * 100;
                const latencyColor = device.latency > 200 ? '#dc3545' : '#ffc107';

                return (
                  <div key={device.id} className="latency-bar-row">
                    <div className="latency-device-name" title={`${device.name} - ${device.station}`}>
                      {device.name}
                    </div>
                    <div className="latency-bar-container">
                      <div
                        className="latency-bar-fill"
                        style={{
                          width: `${latencyPercent}%`,
                          backgroundColor: latencyColor
                        }}
                      />
                    </div>
                    <div className="latency-value" style={{ color: latencyColor }}>
                      {device.latency}ms
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {highLatencyDevices.length > 0 && (
          <div className="chart-footer">
            <span className="footer-text">
              {highLatencyDevices.length} device(s) with latency &gt; 100ms
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoIPCharts;
